import { useState } from "react";
import { router, usePage } from "@inertiajs/react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { Button } from "@/components/ui/button";
import type { ChatbotPreviewData } from "@/components/ChatbotPreviewPanel";
import {
  Sparkles,
  MessageCircle,
  CalendarCheck,
  UserPlus,
  Bot,
  Clock,
} from "lucide-react";
import type { PageProps, AvailabilityRule, Onboarding } from "@/types";

// --- Types ---

interface OnboardingData {
  services: {
    id: number;
    name: string;
    duration_minutes: number;
    price_cents: number;
    emoji: string | null;
  }[];
  staff: {
    id: number;
    name: string;
    pending: boolean;
    color: string | null;
  }[];
  availability: AvailabilityRule[];
  kapso_whatsapp: { status: string; display_address: string | null } | null;
  chatbot_preview: ChatbotPreviewData;
  has_outbound_messages: boolean;
}

interface PeriodStats {
  conversations: number;
  bookings: number;
  new_clients: number;
  agent_messages: number;
}

interface UpcomingBooking {
  id: number;
  starts_at: string;
  ends_at: string | null;
  service_name: string | null;
  service_emoji: string | null;
  client_name: string | null;
  staff_name: string | null;
}

interface DashboardProps extends PageProps {
  onboarding_data?: OnboardingData;
  stats?: {
    today: PeriodStats;
    week: PeriodStats;
    month: PeriodStats;
  };
  upcoming_bookings?: UpcomingBooking[];
}

// --- Constants ---

type Period = "today" | "week" | "month";

const PERIOD_LABELS: Record<Period, string> = {
  today: "Hoy",
  week: "Esta semana",
  month: "Este mes",
};

// --- Main Component ---

export default function DashboardIndex({
  current_user,
  current_organization,
  current_business,
  org_slug,
  billing,
  onboarding_data,
  stats,
  upcoming_bookings,
}: DashboardProps) {
  const { onboarding } = usePage<{ onboarding: Onboarding | null }>().props;
  const [period, setPeriod] = useState<Period>("week");

  if (!current_user || !org_slug) {
    return <div>Loading...</div>;
  }

  const showOnboarding =
    onboarding &&
    !onboarding.complete &&
    !onboarding.dismissed &&
    onboarding_data;
  const showReopenBanner =
    onboarding && !onboarding.complete && onboarding.dismissed;

  const currentStats = stats?.[period];

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={org_slug}
    >
      {showOnboarding ? (
        <OnboardingWizard
          onboarding={onboarding}
          onboardingData={onboarding_data}
          billing={billing}
          orgSlug={org_slug}
          currentBusiness={current_business}
        />
      ) : (
        <div className="space-y-6">
          {/* Reopen onboarding banner */}
          {showReopenBanner && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Tu agente de WhatsApp aún no está configurado
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Completa la configuración para empezar a recibir citas
                    automáticamente.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  router.post(`/${org_slug}/onboarding/reopen`, undefined, {
                    preserveScroll: true,
                  });
                }}
              >
                Continuar configuración
              </Button>
            </div>
          )}

          {/* Header + period filter */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                Inicio
              </h1>
              <p className="text-muted-foreground">
                Bienvenido, {current_user.name || "Usuario"}
              </p>
            </div>
            {stats && (
              <div className="flex rounded-lg border border-border bg-card p-1">
                {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      period === p
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {PERIOD_LABELS[p]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Stats cards */}
          {currentStats && (
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={MessageCircle}
                label="Conversaciones"
                value={currentStats.conversations}
              />
              <StatCard
                icon={CalendarCheck}
                label="Citas agendadas"
                value={currentStats.bookings}
              />
              <StatCard
                icon={UserPlus}
                label="Clientes nuevos"
                value={currentStats.new_clients}
              />
              <StatCard
                icon={Bot}
                label="Mensajes del agente"
                value={currentStats.agent_messages}
              />
            </div>
          )}

          {/* Upcoming bookings */}
          {upcoming_bookings && upcoming_bookings.length > 0 && (
            <div className="rounded-xl border border-border bg-card">
              <div className="border-b border-border p-4">
                <h2 className="font-medium text-foreground">
                  Próximas citas
                </h2>
              </div>
              <div className="divide-y divide-border">
                {upcoming_bookings.map((booking) => (
                  <BookingRow
                    key={booking.id}
                    booking={booking}
                    timezone={current_business?.timezone || "America/Mexico_City"}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state when no upcoming bookings */}
          {upcoming_bookings && upcoming_bookings.length === 0 && (
            <div className="rounded-xl border border-border bg-card p-8 text-center">
              <CalendarCheck className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No tienes citas próximas
              </p>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

// --- Stat Card ---

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4 flex-shrink-0" />
        <h3 className="text-xs sm:text-sm font-medium truncate">{label}</h3>
      </div>
      <p className="mt-2 text-2xl sm:text-3xl font-semibold text-card-foreground">
        {value}
      </p>
    </div>
  );
}

// --- Booking Row ---

function BookingRow({
  booking,
  timezone,
}: {
  booking: {
    id: number;
    starts_at: string;
    ends_at: string | null;
    service_name: string | null;
    service_emoji: string | null;
    client_name: string | null;
    staff_name: string | null;
  };
  timezone: string;
}) {
  const startsAt = new Date(booking.starts_at);
  const endsAt = booking.ends_at ? new Date(booking.ends_at) : null;

  const dateStr = startsAt.toLocaleDateString("es-MX", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: timezone,
  });

  const timeStr = startsAt.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  });

  const endTimeStr = endsAt
    ? endsAt.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
      })
    : null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground sm:w-40 flex-shrink-0">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <span>
          {dateStr} {timeStr}
          {endTimeStr && ` - ${endTimeStr}`}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground">
          {booking.service_emoji && `${booking.service_emoji} `}
          {booking.service_name || "Servicio"}
        </span>
        {booking.client_name && (
          <span className="text-sm text-muted-foreground">
            {" — "}
            {booking.client_name}
          </span>
        )}
      </div>
      {booking.staff_name && (
        <span className="text-xs text-muted-foreground">
          {booking.staff_name}
        </span>
      )}
    </div>
  );
}
